import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Compass, PlusCircle, Bell, User, Zap, Search } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Jixxeo Power shell — mobile-first, dedicated chrome, blue/white/black brand.
 * Applied around every /power/* route. Independent of the parent Jixxeo Apps navbar.
 */
export default function PowerLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");

  // Force a light-theme background inside /power/* by tagging <body>.
  useEffect(() => {
    document.body.classList.add("power-scope");
    return () => document.body.classList.remove("power-scope");
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const trimmed = q.trim();
    nav(trimmed ? `/power/explore?q=${encodeURIComponent(trimmed)}` : "/power/explore");
  };

  const showSearch = loc.pathname === "/power" || loc.pathname === "/power/explore";

  return (
    <div data-testid="power-shell" className="min-h-screen bg-[#F6F8FC] text-[#0A1A2F] pb-24 relative">
      {/* Top brand bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E4EAF3]">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/power" data-testid="power-logo" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E88FF] to-[#0A1A2F] flex items-center justify-center shadow-md">
              <Zap className="w-5 h-5 text-white" fill="white" />
            </div>
            <div className="leading-tight">
              <div className="font-black text-[17px] tracking-tight text-[#0A1A2F]">Jixxeo <span className="text-[#1E88FF]">Power</span></div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1A2F]/50">petitions that move</div>
            </div>
          </Link>
          {showSearch && (
            <form onSubmit={submitSearch} className="ml-auto flex-1 max-w-sm hidden sm:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A1A2F]/40" />
                <input data-testid="power-topbar-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search petitions…"
                  className="w-full pl-9 pr-3 py-2 rounded-full bg-[#F0F4FB] border border-[#E4EAF3] text-sm focus:outline-none focus:border-[#1E88FF]" />
              </div>
            </form>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pt-4">
        <Outlet />
      </main>

      {/* Sticky bottom nav */}
      <nav data-testid="power-bottom-nav"
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#E4EAF3] shadow-[0_-4px_20px_rgba(10,26,47,0.06)]">
        <div className="max-w-4xl mx-auto grid grid-cols-5">
          <BottomTab to="/power" end icon={<Home className="w-5 h-5" />} label="Home" testId="power-nav-home" />
          <BottomTab to="/power/explore" icon={<Compass className="w-5 h-5" />} label="Explore" testId="power-nav-explore" />
          <BottomTab to="/power/create" icon={<PlusCircle className="w-6 h-6" />} label="Create" testId="power-nav-create" primary />
          <BottomTab to="/power/notifications" icon={<Bell className="w-5 h-5" />} label="Alerts" testId="power-nav-notifications" />
          <BottomTab to="/power/profile" icon={<User className="w-5 h-5" />} label="Profile" testId="power-nav-profile" />
        </div>
      </nav>

      <PowerScopedStyles />
    </div>
  );
}

function BottomTab({ to, end, icon, label, testId, primary }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) => [
        "flex flex-col items-center justify-center gap-0.5 py-2 transition-colors",
        isActive ? "text-[#1E88FF]" : "text-[#0A1A2F]/60 hover:text-[#0A1A2F]",
        primary ? "relative" : "",
      ].join(" ")}>
      {primary ? (
        <div className="w-11 h-11 -mt-6 rounded-full bg-gradient-to-br from-[#1E88FF] to-[#0A1A2F] text-white shadow-lg flex items-center justify-center">
          {icon}
        </div>
      ) : (
        icon
      )}
      <span className={"text-[10px] font-semibold tracking-wide " + (primary ? "mt-0" : "")}>{label}</span>
    </NavLink>
  );
}

// Scoped CSS overrides so /power/* looks fully light-mode without touching global styles.
function PowerScopedStyles() {
  return (
    <style>{`
      body.power-scope { background:#F6F8FC !important; color:#0A1A2F !important; }
      body.power-scope .noise-overlay, body.power-scope .grid-backdrop { display:none !important; }
    `}</style>
  );
}
